import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import IgAccountOptions from '../components/IgAccountOptions';
import PlatformBadge from '../components/PlatformBadge';
import PostProgressBar from '../components/PostProgressBar';
import PublishConfirmModal from '../components/PublishConfirmModal';
import apiClient from '../services/api';
import usePostProgress from '../services/usePostProgress';
import useResource from '../services/useResource';
import { AccountGroup, MaybePaginated, Post, PublishResult, SocialAccount } from '../types/api';
import { ProgressEvent } from '../types/progress';
import './PostsPage.css';

export default function DashboardPage() {
  const [actionError, setActionError] = useState<string | null>(null);
  const [publishingId, setPublishingId] = useState<number | null>(null);

  /* The wizard fires its publish then lands here with ?publishing=<id>
   * — adopt that post's progress stream so the user sees the publish
   * running instead of a stale "draft" snapshot. Param is consumed
   * (removed from the URL) so a refresh doesn't re-attach to a
   * finished task. */
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const raw = searchParams.get('publishing');
    if (!raw) return;
    const id = Number(raw);
    if (Number.isFinite(id) && id > 0) setPublishingId(id);
    const next = new URLSearchParams(searchParams);
    next.delete('publishing');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [repostingId, setRepostingId] = useState<number | null>(null);
  const [repostAccounts, setRepostAccounts] = useState<number[]>([]);
  /* Instagram per-account options for the repost, same as the post flow:
   * collaborators get invited on the primary account's post; stories also
   * publish the media as a Story. */
  const [repostIgCollaborators, setRepostIgCollaborators] = useState<number[]>([]);
  const [repostIgStories, setRepostIgStories] = useState<number[]>([]);
  const [allAccounts, setAllAccounts] = useState<SocialAccount[]>([]);
  const [accountGroups, setAccountGroups] = useState<AccountGroup[]>([]);

  const { data: posts, loading, error, reload } = useResource<Post[], MaybePaginated<Post>>(
    () => apiClient.get<MaybePaginated<Post>>('/posts/posts/'),
    [],
    { initial: [], transform: (data) => (Array.isArray(data) ? data : data.results || []) },
  );

  const onDone = useCallback((_event: ProgressEvent) => {
    setPublishingId(null);
    reload();
  }, [reload]);

  const onError = useCallback((_event: ProgressEvent) => {
    setPublishingId(null);
    reload();
  }, [reload]);

  const progressEvent = usePostProgress(publishingId, { onDone, onError });

  async function confirmPublish(): Promise<void> {
    const id = confirmingId;
    if (!id) return;
    setConfirmingId(null);
    setActionError(null);
    setPublishingId(id);
    try {
      await apiClient.post(`/posts/posts/${id}/publish/`, {});
    } catch (err) {
      setActionError((err as Error).message || 'Publish failed');
      setPublishingId(null);
    }
  }

  async function remove(id: number): Promise<void> {
    if (!window.confirm('Delete this post?')) return;
    try {
      await apiClient.delete(`/posts/posts/${id}/`);
      reload();
    } catch (err) {
      setActionError((err as Error).message || 'Delete failed');
    }
  }

  async function deleteFromPlatform(postId: number, resultId: number, accountName: string): Promise<void> {
    if (!window.confirm(`Remove this post from ${accountName}?`)) return;
    try {
      await apiClient.post(`/posts/posts/${postId}/delete_from_platform/`, { result_id: resultId });
      reload();
    } catch (err: any) {
      setActionError(err?.body?.error || err?.message || 'Delete from platform failed');
    }
  }

  async function openRepost(postId: number): Promise<void> {
    setRepostingId(postId);
    setRepostAccounts([]);
    /* Seed the Instagram toggles from the post's saved options so a
     * repost starts from the same collaborator/Story setup the post had. */
    const ig = posts.find((p) => p.id === postId)?.platform_options?.instagram;
    setRepostIgCollaborators(ig?.collaborators || []);
    setRepostIgStories(ig?.stories || []);
    try {
      const [accs, grps] = await Promise.all([
        apiClient.get<MaybePaginated<SocialAccount>>('/accounts/social-accounts/'),
        apiClient.get<MaybePaginated<AccountGroup>>('/accounts/groups/'),
      ]);
      const list = Array.isArray(accs) ? accs : accs.results || [];
      setAllAccounts(list.filter((a) => a.is_active));
      setAccountGroups(Array.isArray(grps) ? grps : grps.results || []);
    } catch {
      setAllAccounts([]);
      setAccountGroups([]);
    }
  }

  /* Group-header checkbox: select/deselect every listed member. */
  function toggleRepostGroup(memberIds: number[]): void {
    setRepostAccounts((prev) => {
      const allOn = memberIds.every((id) => prev.includes(id));
      if (allOn) return prev.filter((id) => !memberIds.includes(id));
      return Array.from(new Set([...prev, ...memberIds]));
    });
  }

  async function submitRepost(): Promise<void> {
    if (!repostingId || repostAccounts.length === 0) return;
    /* Instagram options, same rules the post flow enforces. Only ids that
     * are both selected and Instagram count. */
    const igSelected = allAccounts
      .filter((a) => a.platform === 'instagram' && repostAccounts.includes(a.id))
      .map((a) => a.id);
    const collaborators = repostIgCollaborators.filter((id) => igSelected.includes(id));
    const stories = repostIgStories.filter((id) => igSelected.includes(id));
    if (collaborators.length > 3) {
      setActionError('Instagram allows at most 3 collaborators per post.');
      return;
    }
    if (collaborators.length > 0 && collaborators.length === igSelected.length) {
      setActionError('At least one Instagram account must stay as the primary poster (not a collaborator).');
      return;
    }
    setActionError(null);
    setPublishingId(repostingId);
    const postId = repostingId;
    setRepostingId(null);
    try {
      await apiClient.post(`/posts/posts/${postId}/repost/`, {
        account_ids: repostAccounts,
        /* Send options whenever any Instagram account is in the repost so
         * the modal is authoritative — clearing a toggle clears it on the
         * post too. Omit entirely when no IG account is involved. */
        ...(igSelected.length ? { platform_options: { instagram: { collaborators, stories } } } : {}),
      });
    } catch (err) {
      setActionError((err as Error).message || 'Repost failed');
      setPublishingId(null);
    }
  }

  function toggleRepostAccount(id: number): void {
    setRepostAccounts((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function toggleRepostIg(kind: 'collaborators' | 'stories', id: number): void {
    const setter = kind === 'collaborators' ? setRepostIgCollaborators : setRepostIgStories;
    setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  const errorMessage = actionError || error?.message;

  function thumbnail(p: Post): { url: string; kind: 'image' | 'video' } | null {
    const img = p.media?.find((m) => m.media_type === 'image');
    if (img) return { url: img.file, kind: 'image' };
    const vid = p.media?.find((m) => m.media_type === 'video');
    if (vid) return { url: vid.file, kind: 'video' };
    return null;
  }

  return (
    <div className="posts-page">
      <h2>Posts</h2>
      {errorMessage && <div className="banner banner--error">{errorMessage}</div>}
      {loading && <p className="muted">Loading…</p>}
      {!loading && posts.length === 0 && <p className="muted">No posts yet.</p>}

      <ul className="post-list">
        {posts.map((p) => {
          const thumb = thumbnail(p);
          /* publish_results is an append-only attempt history — show
             only each account's LATEST attempt so a successful retry
             replaces its earlier ✗ instead of standing next to it. */
          const latestByAccount = new Map<number, PublishResult>();
          for (const r of p.publish_results || []) {
            const prev = latestByAccount.get(r.account);
            if (!prev || r.id > prev.id) latestByAccount.set(r.account, r);
          }
          const results = Array.from(latestByAccount.values());
          const successes = results.filter((r) => r.ok);
          const failures = results.filter((r) => !r.ok);

          return (
            <li key={p.id} className={`post-card status-${p.status}`}>
              <div className="post-card-head">
                <span className={`status-badge status-${p.status}`}>{p.status}</span>
                <span className="muted">· {new Date(p.created_at).toLocaleString()}</span>
                {results.length > 0 && (
                  <div className="post-card-results">
                    {successes.map((r) => (
                      <button
                        key={r.id}
                        className="post-result post-result--ok"
                        title={`${r.account_name} — click to remove from platform`}
                        onClick={() => deleteFromPlatform(p.id, r.id, r.account_name)}
                      >
                        <PlatformBadge platform={r.platform} />
                        <span className="post-result-icon">✓</span>
                      </button>
                    ))}
                    {failures.map((r) => (
                      <span key={r.id} className="post-result post-result--fail" title={r.error || r.account_name}>
                        <PlatformBadge platform={r.platform} />
                        <span className="post-result-icon">✗</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="post-card-content">
                {thumb && (
                  <div className="post-card-thumb">
                    {/* A video URL in an <img> renders the broken-image
                        icon (Safari especially); use a metadata-preload
                        <video> so the first frame shows instead. */}
                    {thumb.kind === 'video' ? (
                      <video src={thumb.url} muted playsInline preload="metadata" />
                    ) : (
                      <img src={thumb.url} alt="" />
                    )}
                  </div>
                )}
                <div className="post-card-right">
                  <div className="post-card-body">{p.description}</div>
                  <div className="post-card-actions">
                    {p.status !== 'posted' && (
                      <button
                        className="btn btn--primary btn--sm"
                        onClick={() => setConfirmingId(p.id)}
                        disabled={publishingId === p.id}
                      >
                        {publishingId === p.id ? 'Publishing…' : 'Publish'}
                      </button>
                    )}
                    <button className="btn btn--sm" onClick={() => openRepost(p.id)}>
                      Repost
                    </button>
                    <button className="btn btn--danger btn--sm" onClick={() => remove(p.id)}>
                      Delete
                    </button>
                  </div>
                </div>
              </div>

              {publishingId === p.id && <PostProgressBar event={progressEvent} />}
            </li>
          );
        })}
      </ul>

      <PublishConfirmModal
        postId={confirmingId}
        onClose={() => setConfirmingId(null)}
        onConfirm={confirmPublish}
      />

      {repostingId && (
        <div className="modal is-open" onClick={() => setRepostingId(null)}>
          <div className="modal__panel" onClick={(e) => e.stopPropagation()}>
            <div className="modal__head">
              <h3 className="modal__title">Repost to accounts</h3>
              <button className="btn btn--icon-sm ghost" onClick={() => setRepostingId(null)}>×</button>
            </div>
            <div className="modal__body">
              {allAccounts.length === 0 && <p className="muted">No accounts available.</p>}
              {(() => {
                /* Accounts this post already landed on (any ok publish
                   result) collapse out of the way — reposting usually
                   targets the ones that failed or were skipped. */
                const post = posts.find((p) => p.id === repostingId);
                const postedIds = new Set(
                  (post?.publish_results || []).filter((r) => r.ok).map((r) => r.account),
                );
                const fresh = allAccounts.filter((a) => !postedIds.has(a.id));
                const posted = allAccounts.filter((a) => postedIds.has(a.id));
                const igSelectedCount = allAccounts.filter(
                  (a) => a.platform === 'instagram' && repostAccounts.includes(a.id),
                ).length;
                const row = (a: SocialAccount) => (
                  <li key={a.id}>
                    <label className="repost-account-row">
                      <input
                        type="checkbox"
                        checked={repostAccounts.includes(a.id)}
                        onChange={() => toggleRepostAccount(a.id)}
                      />
                      <PlatformBadge platform={a.platform} />
                      <span>{a.account_name}</span>
                    </label>
                    <IgAccountOptions
                      account={a}
                      selected={repostAccounts.includes(a.id)}
                      igSelectedCount={igSelectedCount}
                      isCollaborator={repostIgCollaborators.includes(a.id)}
                      isStory={repostIgStories.includes(a.id)}
                      onToggleCollaborator={() => toggleRepostIg('collaborators', a.id)}
                      onToggleStory={() => toggleRepostIg('stories', a.id)}
                    />
                  </li>
                );
                /* Mirror the wizard's accounts step: fresh accounts sit
                   under their account groups (group checkbox selects the
                   whole group), loose accounts after. */
                const freshIds = new Set(fresh.map((a) => a.id));
                const groupBlocks = accountGroups
                  .map((g) => ({ g, members: g.accounts.filter((a) => freshIds.has(a.id)) }))
                  .filter((b) => b.members.length > 0);
                const groupedIds = new Set(groupBlocks.flatMap((b) => b.members.map((a) => a.id)));
                const looseFresh = fresh.filter((a) => !groupedIds.has(a.id));
                return (
                  <>
                    <ul className="repost-account-list">
                      {groupBlocks.map(({ g, members }) => {
                        const ids = members.map((a) => a.id);
                        const allOn = ids.every((id) => repostAccounts.includes(id));
                        const someOn = ids.some((id) => repostAccounts.includes(id));
                        return (
                          <li key={`g-${g.id}`} className="repost-group">
                            <label className="repost-account-row repost-group-header">
                              <input
                                type="checkbox"
                                checked={allOn}
                                ref={(el) => { if (el) el.indeterminate = someOn && !allOn; }}
                                onChange={() => toggleRepostGroup(ids)}
                              />
                              <span className="repost-group-name">{g.name}</span>
                              <span className="muted">{members.length}</span>
                            </label>
                            <ul className="repost-account-list repost-group-members">
                              {members.map(row)}
                            </ul>
                          </li>
                        );
                      })}
                      {looseFresh.map(row)}
                    </ul>
                    {fresh.length === 0 && posted.length > 0 && (
                      <p className="muted">This post reached every account — expand below to repost anyway.</p>
                    )}
                    {posted.length > 0 && (
                      <details className="repost-posted-group">
                        <summary className="muted">
                          Already posted ({posted.length}) — repost anyway
                        </summary>
                        <ul className="repost-account-list">{posted.map(row)}</ul>
                      </details>
                    )}
                  </>
                );
              })()}
            </div>
            <div className="modal__foot">
              <button
                className="btn btn--primary"
                disabled={repostAccounts.length === 0}
                onClick={submitRepost}
              >
                Repost to {repostAccounts.length} account{repostAccounts.length !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
